import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface DeviceMockupProps {
  type: 'phone' | 'desktop';
  children?: React.ReactNode;
  className?: string;
}

export function DeviceMockup({ type, children, className }: DeviceMockupProps) {
  if (type === 'phone') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className={cn("relative", className)}
      >
        {/* Glow effect */}
        <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
        
        {/* Phone frame */}
        <div className="relative bg-card border-4 border-muted rounded-[3rem] p-2 shadow-2xl">
          {/* Notch */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-muted rounded-full" />
          
          {/* Screen */}
          <div className="relative bg-background rounded-[2.5rem] overflow-hidden w-[280px] h-[580px] sm:w-[320px] sm:h-[660px]">
            {children || <PhoneMockupContent />}
          </div>
          
          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-muted-foreground/30 rounded-full" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className={cn("relative", className)}
    >
      {/* Glow effect */}
      <div className="absolute -inset-8 bg-primary/15 blur-3xl rounded-3xl" />
      
      {/* Desktop frame */}
      <div className="relative bg-card border-4 border-muted rounded-2xl overflow-hidden shadow-2xl">
        {/* Browser bar */}
        <div className="bg-muted px-4 py-2 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-destructive/60" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
            <div className="w-3 h-3 rounded-full bg-green-500/60" />
          </div>
          <div className="flex-1 max-w-xs mx-auto">
            <div className="bg-background/50 rounded-md px-3 py-1 text-xs text-muted-foreground text-center">
              luma-todo.xyz
            </div>
          </div>
        </div>
        
        {/* Screen */}
        <div className="bg-background w-[600px] h-[400px] lg:w-[800px] lg:h-[500px]">
          {children}
        </div>
      </div>
    </motion.div>
  );
}

function PhoneMockupContent() {
  return (
    <div className="h-full bg-background p-6 pt-12 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/20" />
          <div className="w-20 h-4 bg-muted rounded" />
        </div>
        <div className="w-8 h-8 rounded-full bg-muted" />
      </div>
      
      {/* The ONE Thing card */}
      <div className="relative mb-6">
        <div className="absolute -inset-2 bg-primary/20 blur-xl rounded-2xl" />
        <div className="relative bg-card border border-primary/50 rounded-2xl p-4 shadow-lg">
          <div className="text-xs text-primary font-medium mb-2">THE ONE THING</div>
          <div className="w-full h-5 bg-muted rounded mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary/20" />
              <div className="w-16 h-3 bg-muted rounded" />
            </div>
            <div className="px-2 py-1 bg-primary/20 rounded-full">
              <div className="w-10 h-3 bg-primary/40 rounded" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Task list */}
      <div className="space-y-3 flex-1">
        <div className="text-xs text-muted-foreground mb-2">TODAY</div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border/50">
            <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
            <div className="flex-1">
              <div className="w-full h-4 bg-muted rounded mb-1" style={{ width: `${70 + i * 10}%` }} />
              <div className="w-16 h-2 bg-muted/50 rounded" />
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom nav */}
      <div className="flex justify-around py-4 border-t border-border/50">
        <div className="w-6 h-6 rounded bg-primary/30" />
        <div className="w-6 h-6 rounded bg-muted" />
        <div className="w-6 h-6 rounded bg-muted" />
      </div>
    </div>
  );
}
